// The display language of the PROTEX surface, in one place.
//
// The persona is domain-fluent and schema-illiterate: they know what "energy per
// 100 g" means and have never seen `nutrition.energy_kcal_per_100g`. Every
// function here turns a store-shaped value into something they already read.
//
// They live here rather than in a tab because `AuditTab` importing them from
// `FactsTab` — which imports `FactDetail`, which imports them back — made an
// import cycle out of three formatting functions.

import type { Fact, FactKey, Scope } from "./api";
import { formatIngredients, ingredientsIn } from "./ingredients";

/**
 * How long a value may go unchecked before the surface says so.
 *
 * STATED, not hidden: the "older than expected" chip and its tooltip both name
 * this number, because a threshold a reader cannot see is a threshold they
 * cannot trust. It is the module's own default and NOT a C2 assertion — the
 * fact-key registry declares no per-key expected age, so nothing upstream can
 * be read for it yet. When C2 grows one, this constant is what it replaces.
 */
export const STALE_AFTER_DAYS = 90;

/**
 * A fact's typed value envelope as one readable string.
 *
 * NOTHING HERE MAY RENDER JSON. This function feeds the table cell, the LIVE
 * NOW figure, the correction diff, the confirm dialog and every line of the
 * history, so a shape it cannot read used to surface as `{"items":[{"name":…`
 * on all six at once — which is what Damien saw for `nutrition.active_
 * ingredients` on 12 Sep. The persona is domain-fluent and schema-illiterate
 * (see this file's header); braces are not a value they can check.
 *
 * The compound shapes are read in order of how specifically we know them:
 * the ingredient list has its own prose form, and any other object degrades to
 * its own labelled parts — still readable, still checkable, never markup.
 */
export function formatValue(value: Record<string, unknown>): string {
  if ("n" in value) return String(value.n);
  if ("s" in value) return String(value.s);
  const ingredients = ingredientsIn(value);
  if (ingredients !== null) return formatIngredients(ingredients);
  return labelledParts(value);
}

/**
 * An object envelope of some OTHER shape, as its own parts: "Ml 45 · Servings
 * 3 · Note do not exceed".
 *
 * `nutrition.max_daily_dose` is stored as `{ ml, servings, note }` and more
 * compound keys will follow. This is deliberately a fallback and not a design:
 * a shape that earns a real reading gets one above, and until then the reader
 * still gets words. An empty object says so rather than rendering `{}`.
 */
function labelledParts(value: Record<string, unknown>): string {
  const parts = Object.entries(value)
    .filter(([, part]) => part !== null && part !== undefined && part !== "")
    .map(([label, part]) => `${sentenceCase(label)} ${scalar(part)}`);
  return parts.length === 0 ? "Not set" : parts.join(" · ");
}

function scalar(part: unknown): string {
  if (typeof part === "number" || typeof part === "string" || typeof part === "boolean") {
    return String(part);
  }
  // A nested structure has no honest one-line reading. Saying how many parts it
  // holds is true; printing its markup is not a reading at all.
  return Array.isArray(part) ? `${part.length} entries` : "a set of values";
}

/**
 * A number never renders without its unit anywhere on this surface (UX unit
 * rule, HARD). `899` and `899 kcal` are different claims.
 *
 * A fact with no value at all renders **"Not set"** — never a blank and never a
 * dash. A blank cell reads as "fine", and that is the exact state that let a
 * wrong panel sit unnoticed for 3.8 years.
 */
export function valueWithUnit(
  value: Record<string, unknown> | null,
  unit: string | null,
): string {
  if (value === null) return "Not set";
  const rendered = formatValue(value);
  if (!unit) return rendered;
  const currency = currencyPrefix(unit);
  return currency ? `${currency}${rendered}` : `${rendered} ${unit}`;
}

/** The symbols a money unit renders as, where the market has one people read. */
const CURRENCY_SYMBOL: Record<string, string> = { GBP: "£", EUR: "€", USD: "$" };

/**
 * Money reads as a PREFIX, never as a trailing code: the mock-up sets the
 * shipping thresholds as `£45`, `€50` and `AED 200`, and "45 GBP" is not how
 * anyone writes a price. A currency with a symbol people read gets the symbol
 * closed up against the figure; one without keeps its code, prefixed and
 * spaced, so `AED 200` still reads as money rather than as a quantity of AED.
 *
 * A unit counts as money when it is three capitals — the ISO-4217 shape. No
 * unit in the registry collides with it: nutrition and logistics units are
 * lowercase (`g`, `ml`, `kcal`, `g/ml`) or words (`days`).
 */
function currencyPrefix(unit: string): string | null {
  if (!/^[A-Z]{3}$/.test(unit)) return null;
  // A NON-BREAKING space: `AED 200` must never wrap between the code and the
  // figure, which would leave a bare `200` starting a line in a narrow cell.
  return CURRENCY_SYMBOL[unit] ?? `${unit} `;
}

/**
 * The number and its unit, as two pieces the pane can set at two sizes.
 *
 * The correction diff reads `899` at 26px with `kcal` at 15px regular beside it,
 * so the figure carries the weight and the unit stays legible without competing.
 * Splitting is done HERE rather than by the caller re-deriving it, because the
 * one rule that matters is that the two halves came from the SAME label: a diff
 * whose number and unit were assembled separately can print a number under the
 * wrong unit, and a wrong unit is a wrong claim.
 *
 * `unit: null` when the label does not actually end in the unit — "Not set",
 * "Could not be read", a string-valued fact — so the caller renders it whole
 * rather than shaving a word off the end of a sentence.
 */
export function splitUnit(
  label: string,
  unit: string | null,
): { value: string; unit: string | null } {
  if (!unit) return { value: label, unit: null };
  const suffix = ` ${unit}`;
  return label.endsWith(suffix)
    ? { value: label.slice(0, -suffix.length), unit }
    : { value: label, unit: null };
}

/**
 * The scope, as the chips that sit under a fact's title.
 *
 * `marketLabel` joins the same information into one string for a TABLE CELL,
 * where a column of dot-separated strings scans fine. The pane has room to name
 * each dimension separately, and the mock-up does: two pills, "All markets" and
 * "All stores", rather than one line the reader has to parse.
 *
 * Region and store are always chipped — those are the two the design shows, and
 * a wildcard there is real information ("this value is true everywhere"). The
 * channel and locale are chipped ONLY when they are narrowed: "All channels"
 * beside "All markets" beside "All stores" is three ways of saying the same
 * nothing, while a specific channel is a constraint the reader must see.
 */
export function scopeChips(scope: Scope): string[] {
  const chips = [
    scope.region === "*" ? "All markets" : prose(scope.region),
    scope.store === "*" ? "All stores" : prose(scope.store),
  ];
  if (scope.channel !== "*") chips.push(prose(scope.channel));
  if (scope.locale !== "*") chips.push(scope.locale);
  return chips;
}

/**
 * The human title of a fact key — the primary label on every row and pane.
 *
 * `fact_key` is a machine identifier, not a label (UX naming rule, HARD). The
 * shipped build led with `nutrition.energy_kcal_per_100g`, which the persona had
 * to decode on every single row. The registry's `description` is the sentence a
 * person wrote; `attribute` is the fallback; the raw key is the last resort, and
 * reaching it means the registry is incomplete, not that the label is optional.
 */
export function humanTitle(factKey: string, key?: FactKey): string {
  const name = key?.name?.trim();
  if (name) return name;
  const description = key?.description?.trim();
  if (description) return description;
  if (key?.attribute) return sentenceCase(key.attribute);
  const tail = factKey.includes(".") ? factKey.slice(factKey.indexOf(".") + 1) : factKey;
  return sentenceCase(tail);
}

/**
 * The title a fact is shown under: its NAME (KET-6326, GATE-494 NM-1 = A).
 *
 * The fact item's own `name` first (the fact routes carry it), then the
 * registry's, then today's title. `name` is absent or null until cvce migration
 * p0011 has deployed, and that window must render exactly as before — never an
 * empty title and never an error.
 */
export function factTitle(
  factKey: string,
  key?: FactKey,
  fact?: { name?: string | null } | null,
): string {
  const own = fact?.name?.trim();
  if (own) return own;
  return humanTitle(factKey, key);
}

/**
 * The registry's `description`, shown as the fact's Definition — but only when
 * it says something the title does not. Without a `name`, the description IS
 * the title, and printing it twice is noise.
 */
export function factDefinition(title: string, key?: FactKey): string | null {
  // The registry's own `definition` field wins if it ever sends one.
  const description = key?.definition?.trim() || key?.description?.trim();
  return description && description !== title.trim() ? description : null;
}

function sentenceCase(raw: string): string {
  const words = raw.replace(/[._-]+/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** The domain heading a group of rows sits under — "Nutrition", not "nutrition". */
export function domainTitle(domain: string): string {
  return sentenceCase(domain);
}

/**
 * The market column. Every part of `Scope` that is not the wildcard appears —
 * CHANNEL INCLUDED. Two facts differing only by channel rendered identically
 * before, which is the same failure as a blank cell: the table said they were
 * the same record when they were not, and the wrong one gets corrected.
 *
 * A fully wildcard scope reads "All markets" rather than "All": the column has a
 * subject, and naming it stops the reader supplying their own.
 */
export function marketLabel(scope: Scope): string {
  return (
    [
      prose(scope.region),
      prose(scope.store),
      prose(scope.channel),
      // The locale is a BCP-47 tag, not prose. `sentenceCase` strips the
      // hyphen, so "en-GB" rendered as "En GB" — a string that is not a locale,
      // cannot be searched for, and does not match what the store holds.
      scope.locale,
    ]
      .filter((part) => part !== "*")
      .join(" · ") || "All markets"
  );
}

/** A scope part read as a word: "amazon" → "Amazon", "GCC" left alone. */
function prose(part: string): string {
  return part.toUpperCase() === part ? part : sentenceCase(part);
}

/**
 * The product a value describes, as the persona reads it.
 *
 * A fact with no formula version is not missing a product — it does not have
 * one. Shipping and policy facts are true for the whole catalogue, so they read
 * **"Every product"**, never a blank the reader has to interpret (UX product
 * rule, HARD).
 */
export function productLabel(fact: Pick<Fact, "product_name" | "formula_version_label">): string {
  if (!fact.product_name) return "Every product";
  const version = formulaLabel(fact.formula_version_label);
  return version ? `${fact.product_name} · ${version}` : fact.product_name;
}

/**
 * The formula version as a phrase — "Formula v7" — from whatever the store
 * happens to hold: "7", "v7", or "Formula v7" already spelled out.
 *
 * The ONE place the word "Formula" is put in front of a version. Callers that
 * built the phrase themselves rendered "Formula Formula v7" the moment the
 * store's own label carried the word, which it does.
 */
export function formulaLabel(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const bare = raw.replace(/^\s*formula\s*/i, "").trim();
  if (!bare) return null;
  return `Formula ${/^v/i.test(bare) ? bare : `v${bare}`}`;
}

/**
 * Every status renders as a sentence in domain language — never a raw enum.
 *
 * `supersededBy` is the version that ACTUALLY replaced this record, read off a
 * sibling we hold. It used to be this record's own version, printed as
 * `version + 1` — a number nobody had looked up, naming a record that need not
 * exist: two corrections in a row leave v3 superseded by v5, and the badge
 * would have sent the reader to v4.
 */
export function statusLabel(status: RowStatus, supersededBy?: number): string {
  switch (status) {
    case "approved":
      return "Approved";
    case "draft":
      return "Draft — not published";
    case "superseded":
      // The version that replaced it, when we can see it: "Superseded" alone
      // leaves the reader hunting for what won, and a guess sends them wrong.
      return supersededBy === undefined ? "Superseded" : `Superseded by v${supersededBy}`;
    case "rejected":
      return "Rejected";
    case "missing":
      // "Never approved" was the RECORD's state, not its meaning. This says what
      // it costs: no number is published for this product at all, so the panel
      // on the storefront has a hole in it.
      return "Missing";
  }
}

export type RowStatus = "approved" | "draft" | "superseded" | "rejected" | "missing";

export function ageOf(iso: string, now: Date = new Date()): string {
  const days = daysSince(iso, now);
  if (days <= 0) return "today";
  if (days === 1) return "1 day ago";
  if (days < 60) return `${days} days ago`;
  return `${Math.floor(days / 30)} months ago`;
}

/** The "last checked" cell. A fact that was never approved says so in words. */
export function lastCheckedLabel(approvedAt: string | null, now: Date = new Date()): string {
  return approvedAt === null ? "never published" : ageOf(approvedAt, now);
}

export function daysSince(iso: string, now: Date = new Date()): number {
  return Math.floor((now.getTime() - new Date(iso).getTime()) / 86_400_000);
}

/**
 * Older than expected — the third needs-attention count.
 *
 * A fact that was NEVER approved is not "old", it is **missing**, and it is
 * already counted there. Counting it twice would inflate both chips and send the
 * operator to the same row from two directions.
 */
export function isOlderThanExpected(approvedAt: string | null, now: Date = new Date()): boolean {
  return approvedAt !== null && daysSince(approvedAt, now) > STALE_AFTER_DAYS;
}

/**
 * The exact timestamp, for the tooltip behind every relative age and for every
 * line of a fact's history.
 *
 * Rendered in UTC, ALWAYS — never the reader's own zone. Without `timeZone` the
 * pane printed one approval as 13:14 on a +04 machine while the shell header
 * printed the same event as 09:14, so a single screen carried two times for one
 * thing that happened once. This is an audit trail read by people in the UK and
 * the GCC: a value's approval time has to be the same string for both of them,
 * or two readers reconciling a panel are comparing different numbers.
 */
export function exactDate(iso: string): string {
  return new Date(iso).toLocaleString("en-GB", {
    timeZone: "UTC",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * "Thursday 4 September" — the day headings the audit feed is navigable by.
 *
 * UTC for the same reason `exactDate` is: a heading in local time puts an event
 * timestamped 23:30 UTC under the NEXT day for a reader in the GCC, and the two
 * readers then disagree about which day a value changed.
 */
export function dayHeading(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    timeZone: "UTC",
    weekday: "long",
    day: "numeric",
    month: "long",
  });
}

/**
 * The calendar day an event belongs to, as a stable grouping key.
 *
 * UTC, and it MUST stay whichever zone `dayHeading` renders in — a key cut on a
 * different clock from the heading it groups under files events beneath a day
 * they do not belong to. Both were local until timestamps were pinned to UTC
 * (see `exactDate` for why); they moved together, and they have to keep moving
 * together.
 */
export function dayKey(iso: string): string {
  return new Date(iso).toISOString().slice(0, 10);
}

export function timeOfDay(iso: string): string {
  // UTC, with `exactDate` and `dayHeading` — one clock across the whole surface.
  return new Date(iso).toLocaleTimeString("en-GB", {
    timeZone: "UTC",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * One audit event as the sentence a person would say.
 *
 * "Damien approved Energy per 100 g on MCT Oil C8 — 899 kcal (was 900 kcal)".
 * The product is named because a nutrition number without one is unreadable; it
 * is OMITTED rather than guessed when the fact behind the event has not
 * resolved, because a sentence naming the wrong product is worse than one
 * naming none.
 */
export function eventSentence(event: EventSentenceInput): string {
  const p = eventSentenceParts(event);
  const on = p.product ? ` on ${p.product}` : "";
  const head = `${p.actor} ${p.verb} ${p.title}${on}`;
  if (!p.value) return head;
  return `${head} — ${p.value} ${p.was}`;
}

type EventSentenceInput = {
  action: "approve" | "reject" | "supersede";
  actor: string;
  title: string;
  product?: string | null;
  value?: string | null;
  priorValue?: string | null;
};

/**
 * The same sentence, in the pieces the feed sets at different weights.
 *
 * The approved mock-up bolds the actor, the fact and the product — the three
 * things a reader scans a trail for — and greys "(was …)", which is context for
 * the value rather than a claim of its own. One flat run of text gives the eye
 * nothing to land on, so the renderer needs the parts, not the joined string.
 */
export function eventSentenceParts(event: EventSentenceInput): {
  actor: string;
  verb: string;
  title: string;
  product: string | null;
  value: string | null;
  was: string | null;
  /** `was` is a replacement ("replaced 15 ml"), set in ink, not an aside. */
  replaced: boolean;
} {
  const verb = { approve: "approved", reject: "rejected", supersede: "superseded" }[event.action];
  // A supersede is one value REPLACING another, and the mock-up says it that
  // way: "10 ml replaced 15 ml", in the sentence's own ink. "(was 15 ml)" is the
  // approval's aside — grey context for a number that went live — and on a
  // supersede it read as the same kind of event as the approval above it.
  const replaced = event.action === "supersede" && Boolean(event.value && event.priorValue);
  // "First value", never a blank — a blank reads as "nothing changed".
  const was = event.value
    ? event.priorValue
      ? replaced
        ? `replaced ${event.priorValue}`
        : `(was ${event.priorValue})`
      : "(first value)"
    : null;
  return {
    actor: event.actor,
    verb,
    title: event.title,
    product: event.product ?? null,
    value: event.value ?? null,
    was,
    replaced,
  };
}

/**
 * What each approval in ONE fact's history published, by event id.
 *
 * `ApprovalEventOut` carries the value an approval REPLACED (`prior_value`) but
 * not the value it published. The published value is still recoverable without
 * guessing: C2 writes an approve event on the candidate's own row, so the
 * newest approval on a fact published that fact's value, and every older
 * approval published exactly what the next approval replaced. Drafts
 * (`supersede`) and rejections change nothing live, so they are skipped.
 *
 * An approval whose value cannot be recovered this way is simply absent from
 * the map — the history then says what it can rather than printing a number
 * nobody approved.
 */
export function approvedValuesFor(
  events: ReadonlyArray<{
    event_id: string;
    action: string;
    prior_value: Record<string, unknown> | null;
    created_at: string;
  }>,
  fact: { value: Record<string, unknown>; status: string } | null,
): Map<string, Record<string, unknown>> {
  const approvals = events
    .filter((event) => event.action === "approve")
    .sort((a, b) => b.created_at.localeCompare(a.created_at));
  const values = new Map<string, Record<string, unknown>>();
  // Only a record that has itself been approved has a newest approval to name.
  let next: Record<string, unknown> | null =
    fact && (fact.status === "approved" || fact.status === "superseded") ? fact.value : null;
  for (const approval of approvals) {
    if (next !== null) values.set(approval.event_id, next);
    next = approval.prior_value;
  }
  return values;
}
